import React, { useMemo } from 'react'
import { ProductPermission, MarketType, ProductRight } from 'api'
import { productSectorRecordSelector, productRecordSelector } from 'selectors/productSelectors'
import { useAsRef } from 'view/hooks'
import { useSelector } from 'react-redux'
import styled from '@emotion/styled'
import { ProductsPanelGrid, ProductsPanelGridRow } from 'view/shared/datagrid'
import { RadioButtonStrip } from 'view/components/buttons'
import { Translation } from 'locale'
import { DialogRow } from 'view/components/inputs'

export interface ProductsPanelProps {
  productRights: ProductRight[]
  readOnly: boolean
  onUpdate: (productRights: ProductRight[]) => void
  hideWritePermission?: boolean
  hideViewPermission?: boolean
}

const Wrapper = styled.div`
  height: 430px;
  display: flex;
  flex-direction: column;
  gap: 20px;
`

export const ProductRightsPanel: React.FC<ProductsPanelProps> = ({
  hideWritePermission = false,
  hideViewPermission = false,
  productRights,
  readOnly,
  onUpdate
}) => {
  const [marketType, setMarketType] = React.useState<MarketType>(MarketType.PRIMARY)
  const productSectors = useSelector(productSectorRecordSelector)
  const products = useSelector(productRecordSelector)
  const productRightsRef = useAsRef(productRights)

  const rowData = useMemo<ProductsPanelGridRow[]>(() => {
    return Object.keys(productSectors).map(productSectorId => {
      const productRight = productRights.find(r => r.productSectorId === productSectorId && marketType === r.marketType)

      return {
        productSector: productSectors[productSectorId],
        product: products[productSectors[productSectorId].productId],
        permission: productRight?.permission ?? null
      }
    })
  }, [marketType, productSectors, products, productRights])

  const handleUpdateProductSectors = (productSectorIds: string[], permission: ProductPermission) => {
    const notInRights = productSectorIds.filter(id => !productRights.some(r => r.productSectorId === id && marketType === r.marketType))

    const updatedProductRights = productRightsRef.current
      .map<ProductRight>(r => {
        if (productSectorIds.includes(r.productSectorId) && marketType === r.marketType) {
          return { ...r, permission }
        }

        return r
      })
      .concat(
        notInRights.map(id => ({
          productSectorId: id,
          marketType,
          permission
        }))
      )

    onUpdate(updatedProductRights)
  }

  return (
    <Wrapper>
      <DialogRow>
        <RadioButtonStrip
          dataTestId={'ProductRightsPanel-RadioButtonStripField-MarketType'}
          options={[Translation.TC_PRIMARY_MARKET_TYPE, Translation.TC_SECONDARY_MARKET_TYPE]}
          selectedOption={marketType === MarketType.PRIMARY ? Translation.TC_PRIMARY_MARKET_TYPE : Translation.TC_SECONDARY_MARKET_TYPE}
          onUpdate={translation => {
            setMarketType(translation === Translation.TC_PRIMARY_MARKET_TYPE ? MarketType.PRIMARY : MarketType.SECONDARY)
          }}
          buttonWidth={130}
        />
      </DialogRow>

      <ProductsPanelGrid
        key={marketType}
        rowData={rowData}
        onUpdate={handleUpdateProductSectors}
        disabled={readOnly}
        marketType={marketType}
        hideViewPermission={hideViewPermission}
        hideWritePermission={hideWritePermission}
      />
    </Wrapper>
  )
}
